import React, { useState, useEffect, useContext, useCallback } from 'react';
import { FaBell, FaTimes, FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimesCircle, FaDesktop } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import UserContext from './UserContext';
import { requestPermission, subscribeToPush, unsubscribeFromPush, isPushEnabled, isPushSupported } from '../utils/pushNotifications';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(isPushSupported());
  const { session } = useContext(UserContext);

  const updateAppBadge = useCallback((count) => {
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        navigator.setAppBadge(count).catch(err => console.error('Error setting app badge:', err));
      } else {
        navigator.clearAppBadge().catch(err => console.error('Error clearing app badge:', err));
      }
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!session) {
      // Fallback to localStorage for unauthenticated users
      try {
        const stored = JSON.parse(localStorage.getItem('marketplaceNotifications') || '[]');
        setNotifications(stored);
        const count = stored.filter(n => !n.read).length;
        setUnreadCount(count);
        updateAppBadge(count);
      } catch (error) {
        console.error('Error loading notifications from localStorage:', error);
      }
      return;
    }

    try {
      setLoading(true);
      const response = await HttpInterceptor.get('/api/notifications');
      if (response.data && response.data.success) {
        const fetchedNotifications = response.data.data;
        setNotifications(fetchedNotifications);
        const count = fetchedNotifications.filter(n => !n.read).length;
        
        // Also fetch waitlist count if trainer
        let totalUnread = count;
        if (session.isTrainer) {
          try {
            const waitlistRes = await HttpInterceptor.get('/api/trainer-availability-notifications/trainer/waitlist', {
              params: { isNotified: false, limit: 1 }
            });
            if (waitlistRes.status === 200) {
              const wCount = waitlistRes.data?.data?.pagination?.total || 0;
              setWaitlistCount(wCount);
              totalUnread += wCount;
            }
          } catch (err) {
            console.error('Error fetching waitlist count in NotificationCenter:', err);
          }
        }
        
        setUnreadCount(totalUnread);
        updateAppBadge(totalUnread);
      }
    } catch (error) {
      console.error('Error fetching notifications from API:', error);
    } finally {
      setLoading(false);
    }
  }, [session, updateAppBadge]);

  useEffect(() => {
    loadNotifications();
    
    // Check push status
    if (pushSupported) {
      isPushEnabled().then(setPushEnabled);
    }
    
    // Refresh notifications every 1 minute
    const interval = setInterval(loadNotifications, 60000);
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'marketplaceNotifications' || !e.key) {
        loadNotifications();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadNotifications]);

  const markAsRead = async (id) => {
    if (!session) {
      const updated = notifications.map(n => 
        (n._id === id || n.id === id) ? { ...n, read: true } : n
      );
      setNotifications(updated);
      localStorage.setItem('marketplaceNotifications', JSON.stringify(updated));
      const count = updated.filter(n => !n.read).length;
      setUnreadCount(count);
      updateAppBadge(count);
      return;
    }

    try {
      await HttpInterceptor.patch(`/api/notifications/${id}/read`);
      setNotifications(prev => {
        const updated = prev.map(n => n._id === id ? { ...n, read: true } : n);
        const count = updated.filter(n => !n.read).length;
        setUnreadCount(count);
        updateAppBadge(count);
        return updated;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!session) {
      const updated = notifications.map(n => ({ ...n, read: true }));
      setNotifications(updated);
      localStorage.setItem('marketplaceNotifications', JSON.stringify(updated));
      setUnreadCount(0);
      updateAppBadge(0);
      return;
    }

    try {
      await HttpInterceptor.patch('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      updateAppBadge(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    if (!session) {
      const updated = notifications.filter(n => n._id !== id && n.id !== id);
      setNotifications(updated);
      localStorage.setItem('marketplaceNotifications', JSON.stringify(updated));
      const count = updated.filter(n => !n.read).length;
      setUnreadCount(count);
      updateAppBadge(count);
      return;
    }

    try {
      await HttpInterceptor.delete(`/api/notifications/${id}`);
      setNotifications(prev => {
        const updated = prev.filter(n => n._id !== id);
        const count = updated.filter(n => !n.read).length;
        setUnreadCount(count);
        updateAppBadge(count);
        return updated;
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAll = async () => {
    if (!session) {
      setNotifications([]);
      localStorage.setItem('marketplaceNotifications', '[]');
      setUnreadCount(0);
      updateAppBadge(0);
      return;
    }

    try {
      await HttpInterceptor.delete('/api/notifications');
      setNotifications([]);
      setUnreadCount(0);
      updateAppBadge(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="text-green-600" />;
      case 'error':
        return <FaTimesCircle className="text-red-600" />;
      case 'warning':
        return <FaExclamationCircle className="text-yellow-600" />;
      default:
        return <FaInfoCircle className="text-blue-600" />;
    }
  };

  const handleTogglePush = async () => {
    if (!pushSupported) return;

    try {
      if (pushEnabled) {
        const success = await unsubscribeFromPush();
        if (success) setPushEnabled(false);
      } else {
        const permission = await requestPermission();
        if (permission === 'granted') {
          const subscription = await subscribeToPush();
          if (subscription) setPushEnabled(true);
        }
      }
    } catch (err) {
      console.error('Error toggling push notifications:', err);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-700 hover:text-purple-600 transition-colors"
        aria-label="Notifications"
      >
        <FaBell className="text-xl" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-xl shadow-2xl z-50 border border-gray-200 max-h-96 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
              <h3 className="font-bold text-lg">Notifications {loading && '...'}</h3>
              <div className="flex items-center gap-2">
                {pushSupported && (
                  <button
                    onClick={handleTogglePush}
                    className={`p-1.5 rounded transition-colors ${pushEnabled ? 'bg-white/20 text-white' : 'bg-red-500/20 text-red-200'} hover:bg-white/30`}
                    title={pushEnabled ? 'Push notifications enabled' : 'Enable push notifications'}
                  >
                    <FaDesktop size={14} />
                  </button>
                )}
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm hover:underline"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/20 p-1 rounded"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 && (!session?.isTrainer || waitlistCount === 0) ? (
                <div className="p-8 text-center text-gray-500">
                  <FaBell className="text-4xl mx-auto mb-2 text-gray-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {/* Waitlist Notification (Trainer Only) */}
                  {session?.isTrainer && waitlistCount > 0 && (
                    <div className="p-4 bg-orange-50 border-l-4 border-orange-500 hover:bg-orange-100 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <FaBell className="text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800">
                            Availability Waitlist
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            You have <span className="font-bold text-orange-600">{waitlistCount}</span> students waiting for your programs to become available.
                          </p>
                          <a 
                            href="/trainer/notifications?tab=waitlist"
                            onClick={() => setIsOpen(false)}
                            className="text-xs text-orange-600 hover:underline mt-2 inline-block font-medium"
                          >
                            View Waitlist
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {notifications.map(notification => (
                    <div
                      key={notification._id || notification.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !notification.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          {notification.link && (
                            <a 
                              href={notification.link}
                              onClick={(e) => {
                                e.preventDefault();
                                setIsOpen(false);
                                window.location.href = notification.link;
                                if (!notification.read) markAsRead(notification._id || notification.id);
                              }}
                              className="text-xs text-purple-600 hover:underline mt-2 inline-block font-medium"
                            >
                              View Details
                            </a>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(notification.timestamp || notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification._id || notification.id)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Mark read
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification._id || notification.id)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={clearAll}
                  className="w-full text-sm text-red-600 hover:underline text-center"
                >
                  Clear all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Utility function to add notifications (keeps local fallback)
export const addNotification = (title, message, type = 'info') => {
  const notification = {
    id: Date.now().toString(),
    title,
    message,
    type,
    read: false,
    timestamp: new Date().toISOString()
  };
  
  const existing = JSON.parse(localStorage.getItem('marketplaceNotifications') || '[]');
  const updated = [notification, ...existing].slice(0, 50);
  localStorage.setItem('marketplaceNotifications', JSON.stringify(updated));
  window.dispatchEvent(new Event('storage'));
};

export default NotificationCenter;
